/**
 * Resolves the current viewer's nav-item access — roles, connected accounts and
 * tenant persona — for {@link isNavItemVisible}.
 *
 * One hook so the sidebar, the module picker and global search read the same
 * inputs; the underlying queries are React Query-cached, so the extra consumers
 * cost nothing beyond a cache read.
 */
import { useMemo } from 'react';

import { useGoogleIntegration } from '../users/hooks/useGoogleIntegration';
import { useMicrosoftIntegration } from '../users/hooks/useMicrosoftIntegration';
import { useAuth } from '../auth';
import type { NavItemAccess } from '../utils/navItemAccess';
import { usePersona } from './usePersona';

export function useNavItemAccess(): NavItemAccess {
  const { user } = useAuth();
  const { data: googleStatus } = useGoogleIntegration();
  const { data: microsoftStatus } = useMicrosoftIntegration();
  const { data: persona } = usePersona();

  const isAdmin = user?.is_admin === true;
  const isSysadmin = user?.is_sysadmin === true;
  const isGoogleConnected = googleStatus?.data?.google_connected === true;
  const isMicrosoftConnected = microsoftStatus?.data?.connected === true;
  const sellsFreightServices = persona?.sellsFreightServices === true;

  return useMemo(
    () => ({
      isAdmin,
      isSysadmin,
      isGoogleConnected,
      isMicrosoftConnected,
      sellsFreightServices,
    }),
    [isAdmin, isSysadmin, isGoogleConnected, isMicrosoftConnected, sellsFreightServices],
  );
}
